import * as THREE from 'three';
import { Logger } from '../core/Logger';
import { Scene } from '../core/Scene';

/**
 * 🎯 TextureCache - Singleton für effizientes Texture-Sharing
 * Verhindert mehrfaches Laden derselben Textur bei mehreren POIs
 */
export class TextureCache {
    private static instance: TextureCache | null = null;
    private cache = new Map<string, THREE.Texture>();
    private loadingPromises = new Map<string, Promise<THREE.Texture>>();
    private refCounts = new Map<string, number>();

    private constructor() {
        // 🎯 Make cache stats available globally for debugging
        (window as any).debugTextureCache = () => {
            console.log('🗄️ TextureCache Stats:', this.getStats());
            this.debugPrintCache();
        };
        
        (window as any).clearTextureCache = () => {
            this.clearCache();
            console.log('🧹 TextureCache cleared!');
        };
    }

    public static get Instance(): TextureCache {
        if (!TextureCache.instance) {
            TextureCache.instance = new TextureCache();
            console.log('🗄️ [TextureCache] Singleton initialized');
        }
        return TextureCache.instance;
    }

    /**
     * 🎯 Get texture from cache or load if not cached
     * Multiple calls with same URL will share the same texture instance
     */
    public async getTexture(url: string, loader?: THREE.TextureLoader): Promise<THREE.Texture> {
        if (!url || url.trim() === '') {
            throw new Error('TextureCache: Empty URL provided');
        }

        // Check cache first
        if (this.cache.has(url)) {
            //console.log(`♻️ [TextureCache] Cache HIT: '${url}'`);
            this.incrementRefCount(url);
            return this.cache.get(url)!;
        }

        // Check if already loading
        if (this.loadingPromises.has(url)) {
            //console.log(`⏳ [TextureCache] Already loading: '${url}' - waiting for completion`);
            const texture = await this.loadingPromises.get(url)!;
            this.incrementRefCount(url);
            return texture;
        }

        // Start loading
        //console.log(`📥 [TextureCache] Cache MISS: Loading '${url}'`);
        const loadingPromise = this.loadTextureFromURL(url, loader);
        this.loadingPromises.set(url, loadingPromise);

        try {
            const texture = await loadingPromise;
            
            // Store in cache
            this.cache.set(url, texture);
            this.incrementRefCount(url);

            // NOTE: We deliberately do NOT call renderer.initTexture() here. That's a synchronous
            // GPU upload which blocks the main thread (1-2s for 2500x1406 textures on weaker GPUs).
            // With 32 textures it serialized into 30-60s of frozen UI. Three.js uploads lazily on
            // first use — minimal cost per frame because only a few are visible at once.

            // Clean up loading promise
            this.loadingPromises.delete(url);

            //console.log(`✅ [TextureCache] Cached texture: '${url}' (${texture.image.width}x${texture.image.height})`);
            return texture;
            
        } catch (error) {
            // Clean up failed loading promise
            this.loadingPromises.delete(url);
            console.error(`❌ [TextureCache] Failed to load texture: '${url}'`, error);
            throw error;
        }
    }

    /**
     * 🎯 Load texture using THREE.TextureLoader
     */
    private loadTextureFromURL(url: string, loader?: THREE.TextureLoader): Promise<THREE.Texture> {
        const textureLoader = loader || new THREE.TextureLoader();
        const tStart = performance.now();
        const shortUrl = url.substring(url.lastIndexOf('/')+1).substring(0, 30);
        try { Logger.Instance.Info(`load START ${shortUrl}`, 'TexLoad'); } catch {}

        return new Promise((resolve, reject) => {
            textureLoader.load(
                url,
                (texture: THREE.Texture) => {
                    const totalMs = performance.now() - tStart;
                    // Read precise per-phase timing from Browser's Performance API
                    let phaseStr = '';
                    try {
                        const perfEntries = performance.getEntriesByName(url) as PerformanceResourceTiming[];
                        const entry = perfEntries[perfEntries.length - 1];
                        if (entry) {
                            const dns = entry.domainLookupEnd - entry.domainLookupStart;
                            const tcp = entry.connectEnd - entry.connectStart;
                            const queue = entry.requestStart - entry.startTime;        // queue+stall before request
                            const ttfb = entry.responseStart - entry.requestStart;     // server thinking time
                            const dl = entry.responseEnd - entry.responseStart;        // bytes download
                            const decode = totalMs - (entry.responseEnd - entry.startTime); // <img> decode after bytes ready
                            const size = entry.encodedBodySize;
                            phaseStr = `  [queue:${queue.toFixed(0)} dns:${dns.toFixed(0)} tcp:${tcp.toFixed(0)} ttfb:${ttfb.toFixed(0)} dl:${dl.toFixed(0)} decode:${decode.toFixed(0)}ms  size:${(size/1024).toFixed(0)}KB]`;
                        }
                    } catch {}
                    try { Logger.Instance.Info(`load DONE ${shortUrl} total=${totalMs.toFixed(0)}ms ${texture.image?.width}x${texture.image?.height}${phaseStr}`, 'TexLoad'); } catch {}
                    texture.colorSpace = THREE.SRGBColorSpace;
                    texture.flipY = true;
                    texture.needsUpdate = true;
                    resolve(texture);
                },
                (progress: ProgressEvent) => {
                    // Progress callback - could be used for loading indicators
                },
                (error: unknown) => {
                    const ms = (performance.now() - tStart).toFixed(0);
                    try { Logger.Instance.Warn(`load FAIL ${url} after ${ms}ms`, 'TexLoad'); } catch {}
                    reject(error);
                }
            );
        });
    }

    /**
     * 🎯 Release texture reference (for proper cleanup)
     */
    public releaseTexture(url: string): void {
        if (!this.cache.has(url)) {
            return;
        }

        const currentCount = this.refCounts.get(url) || 0;
        if (currentCount <= 1) {
            // Last reference - dispose and remove from cache
            const texture = this.cache.get(url);
            if (texture) {
                //console.log(`🗑️ [TextureCache] Disposing texture: '${url}' (no more references)`);
                texture.dispose();
            }
            this.cache.delete(url);
            this.refCounts.delete(url);
        } else {
            // Decrement reference count
            this.refCounts.set(url, currentCount - 1);
            //console.log(`📉 [TextureCache] Decremented ref count for '${url}': ${currentCount - 1}`);
        }
    }

    /**
     * 🎯 Increment reference count for a texture
     */
    private incrementRefCount(url: string): void {
        const currentCount = this.refCounts.get(url) || 0;
        this.refCounts.set(url, currentCount + 1);
        //console.log(`📈 [TextureCache] Incremented ref count for '${url}': ${currentCount + 1}`);
    }

    /**
     * 🎯 Get cache statistics
     */
    public getStats(): { cached: number; loading: number; totalRefs: number } {
        const totalRefs = Array.from(this.refCounts.values()).reduce((sum, count) => sum + count, 0);
        return {
            cached: this.cache.size,
            loading: this.loadingPromises.size,
            totalRefs
        };
    }

    /**
     * 🎯 Clear entire cache (use with caution!)
     */
    public clearCache(): void {
        //console.log(`🧹 [TextureCache] Clearing cache (${this.cache.size} textures)`);
        
        // Dispose all textures
        for (const texture of this.cache.values()) {
            texture.dispose();
        }
        
        this.cache.clear();
        this.refCounts.clear();
        this.loadingPromises.clear();
    }

    /**
     * 🎯 Debug: Print cache contents
     */
    public debugPrintCache(): void {
        //console.log(`🔍 [TextureCache] Cache contents (${this.cache.size} textures):`);
        for (const [url, texture] of this.cache.entries()) {
            const refCount = this.refCounts.get(url) || 0;
            //console.log(`  📄 '${url}' - refs: ${refCount}, uuid: ${texture.uuid}`);
        }
    }
}